import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import Navigation from '../components/Navigation';
import PrimaryButton from '../components/PrimaryButton';
import SnowflakesEffect from '../components/SnowflakesEffect';
import useDrinkEffect from '../hooks/useDrinkEffect';
import { isChristmasPeriod } from '../utils/christmasTheme';
import { trackBeerDrink, trackButtonClick, trackVideoEvent, trackPDFDownload, trackLinkCopied } from '../utils/analytics';
import '../styles/ReceiveThanksPage.css';
import '../styles/christmas.css';

// Utility function to decode kudos message from base64
const decodeMessage = (encodedMessage: string): string => {
  try {
    return decodeURIComponent(atob(encodedMessage));
  } catch (e) {
    return encodedMessage;
  }
};

// Utility function to format company name for display
const formatCompanyName = (company: string): string => {
  // Convert hyphens to spaces and capitalize each word
  return company
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Default name constants
const DEFAULT_SENDER_NAME = 'Someone';
const DEFAULT_RECIPIENT_NAME = 'Friend';

const ReceiveThanksPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { company } = useParams<{ company?: string }>();
  const [senderName, setSenderName] = useState(DEFAULT_SENDER_NAME);
  const [recipientName, setRecipientName] = useState(DEFAULT_RECIPIENT_NAME);
  const [kudosMessage, setKudosMessage] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [globalBeerCount, setGlobalBeerCount] = useState<number | null>(null);
  const [localBeerStreak, setLocalBeerStreak] = useState(0);
  const [ambientMessage, setAmbientMessage] = useState('');
  const [showCompletion, setShowCompletion] = useState(false);
  const [showCompletionActions, setShowCompletionActions] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const { drinking, finished, startDrinking, finishDrinking } = useDrinkEffect();
  const wallpaperVideoRef = useRef<HTMLVideoElement>(null);
  const copiedMessageTimeoutRef = useRef<number | null>(null);
  const isChristmas = isChristmasPeriod();
  
  // Constants
  const COPIED_MESSAGE_TIMEOUT = 3000; // milliseconds

  useEffect(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    // Support both old 'message' and new 'msg' (encoded) parameters for backward compatibility
    const message = searchParams.get('message');
    const encodedMsg = searchParams.get('msg');
    const viewOnly = searchParams.get('view');
    
    // Check if this is view-only mode
    if (viewOnly === 'true') {
      setIsViewOnly(true);
      // Auto-finish for view-only mode
      finishDrinking();
      setShowCompletion(true);
      setShowCompletionActions(true);
    }
    
    if (from) {
      setSenderName(from);
    }
    if (to) {
      setRecipientName(to);
    }
    if (encodedMsg) {
      setKudosMessage(decodeMessage(encodedMsg));
    } else if (message) {
      // Fallback to old unencoded message parameter
      setKudosMessage(message);
    }
  }, [searchParams, finishDrinking]);

  // Load global beer count on mount
  useEffect(() => {
    const fetchGlobalCount = async () => {
      try {
        const response = await fetch('/api/beer-counter');
        if (response.ok) {
          const data = await response.json();
          setGlobalBeerCount(data.count);
        }
      } catch (error) {
        console.error('Failed to fetch global beer count:', error);
      }
    };
    fetchGlobalCount();
  }, []);

  // Load local beer streak from localStorage
  useEffect(() => {
    const storedStreak = localStorage.getItem('thanksBeersEnjoyed');
    if (storedStreak) {
      setLocalBeerStreak(parseInt(storedStreak, 10));
    }
  }, []);

  // Reload local streak when finished drinking to ensure latest count is shown
  useEffect(() => {
    if (finished) {
      const storedStreak = localStorage.getItem('thanksBeersEnjoyed');
      if (storedStreak) {
        setLocalBeerStreak(parseInt(storedStreak, 10));
      }
    }
  }, [finished]);

  // Silent Social Proof - Ambient activity messages
  useEffect(() => {
    const locations = [
      'Sydney', 'New York', 'London', 'Tokyo', 'Paris', 'Berlin', 
      'Toronto', 'Amsterdam', 'Singapore', 'San Francisco', 'Mumbai',
      'Seattle', 'Boston', 'Austin', 'Chicago', 'LA', 'Miami'
    ];
    
    const messages = [
      (loc: string) => `Someone in ${loc} just enjoyed a ThanksBeer 🍺`,
      (loc: string) => `Another ThanksBeer was just sent from ${loc}`,
      (loc: string) => `A ThanksBeer is being enjoyed in ${loc} right now 🍺`,
      (loc: string) => `${loc} just sent a ThanksBeer`,
      (loc: string) => `Cheers from ${loc}! 🍺`,
      () => `A new ThanksBeer just landed`,
      (loc: string) => `${loc} is celebrating with a ThanksBeer`,
      () => `Someone just shared gratitude 🍺`,
      (loc: string) => `ThanksBeer vibes from ${loc}`,
      () => `Another round of appreciation sent`,
      (loc: string) => `${loc} is spreading the love 🍺`,
      () => `Gratitude is flowing worldwide`,
    ];

    const updateAmbientMessage = () => {
      const randomLocation = locations[Math.floor(Math.random() * locations.length)];
      const randomMessage = messages[Math.floor(Math.random() * messages.length)];
      const message = randomMessage(randomLocation);
      setAmbientMessage(message);
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setAmbientMessage('');
      }, 5000);
    };

    // Show first message after 2 seconds
    const initialTimer = setTimeout(updateAmbientMessage, 2000);
    
    // Then show messages every 5-30 seconds with random interval
    const scheduleNextMessage = () => {
      const randomInterval = Math.random() * 25000 + 5000; // Random between 5-30 seconds
      return setTimeout(() => {
        updateAmbientMessage();
        scheduleNextMessage();
      }, randomInterval);
    };
    
    const intervalTimer = scheduleNextMessage();

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(intervalTimer);
    };
  }, []);

  useEffect(() => {
    if (drinking && wallpaperVideoRef.current) {
      wallpaperVideoRef.current.currentTime = 0; // Reset to beginning
      wallpaperVideoRef.current.play().catch(err => {
        console.error('Failed to play background wallpaper video:', err);
      });
      
      // Track video start
      trackVideoEvent('start', 'drinking_animation');
      
      // Finish drinking after 7 seconds (duration of drinking animation)
      const timer = setTimeout(() => {
        finishDrinking();
        // Track video completion
        trackVideoEvent('complete', 'drinking_animation');
      }, 7000);
      
      return () => clearTimeout(timer);
    }
  }, [drinking, finishDrinking]);

  // Show kudos message with fade-in after drinking finishes
  useEffect(() => {
    if (finished && kudosMessage) {
      setShowNote(true);
    }
    
    // Show completion state after drinking finishes
    if (finished) {
      // Wait 2 seconds after finishing, then show "This one's done. Cheers."
      const completionTimer = setTimeout(() => {
        setShowCompletion(true);
        
        // After 1 more second, show the action buttons
        const actionsTimer = setTimeout(() => {
          setShowCompletionActions(true);
        }, 1000);
        
        return () => clearTimeout(actionsTimer);
      }, 2000);
      
      return () => clearTimeout(completionTimer);
    }
  }, [finished, kudosMessage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (copiedMessageTimeoutRef.current) {
        clearTimeout(copiedMessageTimeoutRef.current);
      }
    };
  }, []);

  const handleDrinkClick = async () => {
    startDrinking();
    
    // Increment and fetch global beer counter in one call
    try {
      const response = await fetch('/api/beer-counter', { method: 'PUT' });
      if (response.ok) {
        const data = await response.json();
        setGlobalBeerCount(data.count);
        
        // Only increment local streak if global counter succeeds
        const newStreak = localBeerStreak + 1;
        setLocalBeerStreak(newStreak);
        localStorage.setItem('thanksBeersEnjoyed', newStreak.toString());
        
        // Track beer drink event
        trackBeerDrink({
          globalCount: data.count,
          localStreak: newStreak,
          hasSender: senderName !== DEFAULT_SENDER_NAME,
          hasRecipient: recipientName !== DEFAULT_RECIPIENT_NAME,
          hasMessage: !!kudosMessage,
          company,
        });
      }
    } catch (error) {
      console.error('Failed to increment global beer count:', error);
      // Still increment local streak even if API fails
      const newStreak = localBeerStreak + 1;
      setLocalBeerStreak(newStreak);
      localStorage.setItem('thanksBeersEnjoyed', newStreak.toString());
      
      // Track beer drink event even if API fails
      trackBeerDrink({
        globalCount: null,
        localStreak: newStreak,
        hasSender: senderName !== DEFAULT_SENDER_NAME,
        hasRecipient: recipientName !== DEFAULT_RECIPIENT_NAME,
        hasMessage: !!kudosMessage,
        company,
      });
    }
  };

  const handleSendThanksBeer = () => {
    trackButtonClick('Send ThanksBeer', 'ReceiveThanksPage');
    navigate('/');
  };

  // Handler for generating view-only URL
  const handleShareViewOnly = () => {
    const currentUrl = window.location.href;
    const viewOnlyUrl = currentUrl + (currentUrl.includes('?') ? '&' : '?') + 'view=true';
    navigator.clipboard.writeText(viewOnlyUrl);
    
    // Track link copy
    trackLinkCopied(viewOnlyUrl);
    
    // Clear any existing timeout
    if (copiedMessageTimeoutRef.current) {
      clearTimeout(copiedMessageTimeoutRef.current);
    }
    
    // Show feedback message
    setShowCopiedMessage(true);
    copiedMessageTimeoutRef.current = setTimeout(() => {
      setShowCopiedMessage(false);
    }, COPIED_MESSAGE_TIMEOUT) as unknown as number;
  };

  // Handler for exporting as proof of work (PDF)
  const handleExportProof = async () => {
    try {
      // Create a PDF with standard letter size
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      try {
        // Load and add background image with timeout
        const backgroundImg = new Image();
        backgroundImg.crossOrigin = 'anonymous';
        
        // Use the ReceiveThanks background
        backgroundImg.src = '/receivethanks.png';
        
        await Promise.race([
          new Promise((resolve, reject) => {
            backgroundImg.onload = resolve;
            backgroundImg.onerror = () => reject(new Error('Failed to load background image'));
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Image loading timeout')), 10000)
          )
        ]);
        
        // Add background image to fill the page
        pdf.addImage(backgroundImg, 'PNG', 0, 0, pageWidth, pageHeight);
      } catch (imgError) {
        console.warn('Background image failed to load, continuing without it:', imgError);
        // Continue without background - add a solid background color instead
        pdf.setFillColor(26, 20, 16); // Dark brown
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      }
      
      // Add a dark overlay box for the content area to improve readability
      pdf.setFillColor(26, 20, 16, 0.8); // Dark brown with transparency
      const boxX = 20;
      const boxY = 15;
      const boxWidth = pageWidth - 40;
      const boxHeight = pageHeight - 30;
      pdf.rect(boxX, boxY, boxWidth, boxHeight, 'F');
      
      // Add ThanksBeer header
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(32);
      pdf.setTextColor(232, 168, 50); // Golden color
      pdf.text('ThanksBeer', pageWidth / 2, 30, { align: 'center' });
      
      // Add decorative line
      pdf.setDrawColor(232, 168, 50);
      pdf.setLineWidth(0.5);
      pdf.line(30, 40, pageWidth - 30, 40);
      
      // Add message details with better formatting
      const startY = 60;
      const lineHeight = 12;
      let currentY = startY;
      
      // FROM
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(255, 255, 255);
      pdf.text('FROM:', 30, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(18);
      pdf.text(senderName, 60, currentY);
      currentY += lineHeight + 5;
      
      // TO
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text('TO:', 30, currentY);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(18);
      pdf.text(recipientName, 60, currentY);
      currentY += lineHeight + 15;
      
      // MESSAGE (if exists)
      if (kudosMessage) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(232, 168, 50);
        pdf.text('MESSAGE:', 30, currentY);
        currentY += lineHeight;
        
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(16);
        pdf.setTextColor(255, 255, 255);
        
        // Word wrap the message
        const maxWidth = pageWidth - 60;
        const lines = pdf.splitTextToSize(`"${kudosMessage}"`, maxWidth);
        
        lines.forEach((line: string) => {
          pdf.text(line, 30, currentY);
          currentY += lineHeight;
        });
        
        currentY += 10;
      }
      
      // STATS - Only show personal beer count
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(14);
      pdf.setTextColor(255, 255, 255);
      const beerText = localBeerStreak === 1 
        ? `You've enjoyed 1 ThanksBeer`
        : `You've enjoyed ${localBeerStreak} ThanksBeers`;
      pdf.text(beerText, 30, currentY);
      currentY += lineHeight + 15;
      
      // Add timestamp
      const timestamp = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(11);
      pdf.setTextColor(180, 180, 180);
      pdf.text(timestamp, pageWidth / 2, pageHeight - 20, { align: 'center' });
      
      // Add footer
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text('thanksbeer.com', pageWidth / 2, pageHeight - 10, { align: 'center' });
      
      // Save the PDF with sanitized filename (only alphanumeric, underscore, and hyphen)
      const sanitizedName = recipientName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `ThanksBeer_${sanitizedName}_${Date.now()}.pdf`;
      pdf.save(filename);
      
      // Track PDF download
      trackPDFDownload(filename);
      
    } catch (error) {
      console.error('Failed to export PDF:', error);
    }
  };

  return (
    <div className={`receive-thanks-page ${isChristmas ? 'christmas' : ''}`}>
      {isChristmas && <SnowflakesEffect />}
      {isChristmas && <div className="christmas-overlay" />}
      <video
        ref={wallpaperVideoRef}
        className="wallpaper-video"
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      >
        <source src="/beer_drinking_wallpaper.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <Navigation />
      
      {/* Ambient Activity Messages */}
      {ambientMessage && (
        <div className="ambient-message fade-in">
          {ambientMessage}
        </div>
      )}
      
      {/* Copied Message Feedback */}
      {showCopiedMessage && (
        <div className="copied-message fade-in">
          ✓ Link copied to clipboard!
        </div>
      )}
      
      <div className="overlay">
        <div className="content-wrapper">
          <div className="left-content">
            <div className={`content-card ${drinking ? 'drinking' : ''}`}>
              <h1 className="headline">Cheers, {recipientName}.</h1>
                              <div className="finished-message">
                  You recived a ThanksBeer!
                  <img src="/thanksbeer.ico" alt="Beer" className="finished-icon" />

                </div>
              <p className="sender-info">From {senderName}</p>
            
            {/* View-only badge */}
            {isViewOnly && (
              <div className="view-only-badge">
                👁️ View-only mode - Witness this ThanksBeer
              </div>
            )}
            
            {!finished && !isViewOnly ? (
              <>
                <PrimaryButton onClick={handleDrinkClick} disabled={drinking}>
                  {drinking ? 'DRINKING...' : (kudosMessage ? 'CLICK TO DRINK\nTO REVEAL MESSAGE' : 'DRINK')}
                </PrimaryButton>
              </>
            ) : (
              <>
                {/* Show kudos message after drinking */}
                {kudosMessage && showNote && (
                  <div className="kudos-display fade-in">
                    <p className="kudos-message">"{kudosMessage}"</p>
                  </div>
                )}
                
                {kudosMessage && showNote && showCompletion && (
                  <div className="message-separator"></div>
                )}
                
                {showCompletion && showCompletionActions && !isViewOnly && (
                  <>
                    <PrimaryButton onClick={handleSendThanksBeer}>
                      🍺 Carry it on
                    </PrimaryButton>
                    
                    <div className="simple-action-links">
                      <button className="link-action" onClick={handleExportProof}>
                        💾 Save as PDF
                      </button>
                      <span className="link-sep">·</span>
                      <button className="link-action" onClick={handleShareViewOnly}>
                        🔗 Share View Only Link
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Show stats at bottom of card after drinking finishes */}
            {finished && (
              <div className="card-stats inline">
                <div className="stat-item">
                  <img src="/thanksbeer.ico" alt="Beer" className="stat-icon-img" />
                  <span className="stat-text">You've enjoyed {localBeerStreak} ThanksBeer{localBeerStreak !== 1 ? 's' : ''}</span>
                </div>

                <span className="stat-sep" aria-hidden>•</span>

                <div className="stat-item">
                  <span className="stat-icon">🌍</span>
                  <span className="stat-text">{globalBeerCount !== null ? `${globalBeerCount.toLocaleString()} enjoyed worldwide` : '— enjoyed worldwide'}</span>
                </div>
              </div>
            )}
            </div>
          </div>
          
          <div className="right-content">
            {/* Right content removed - kudos now shown in left-content */}
          </div>
        </div>
        
        {/* Global Beer Counter - Bottom of page
        {finished && globalBeerCount !== null && (
          <div className="global-counter-bottom">
            <span className="counter-icon">🍺</span>
            <span className="counter-text">
              {globalBeerCount.toLocaleString()} beers enjoyed worldwide
            </span>
          </div>
        )} */}
        
        {/* Subtle company context - only shown if company is specified */}
        {company && (
          <div className="company-context">
            <p>Shared within {formatCompanyName(company)}</p>
          </div>
        )}
        
        {/* Privacy Disclaimer */}
        <div className="privacy-disclaimer">
          <p>No personal information is collected. Names and messages are purely for gratitude and appreciation.</p>
        </div>
      </div>
    </div>
  );
};

export default ReceiveThanksPage;
